import { Box, Chip, Typography } from "@mui/material";
import React from "react";
import { formatWeekRange, weekKey } from "utils/dailyRewardWeek";

export type CycleRow = {
  _id: string;
  name: string;
  isDefault: boolean;
  startDate?: string | null;
  endDate?: string | null;
  operators?: string[];
  isActive: boolean;
};

export type CyclePhase = "default" | "live" | "upcoming" | "past";

export const PHASE_LABELS: Record<CyclePhase, string> = {
  default: "Default",
  live: "Running now",
  upcoming: "Upcoming",
  past: "Ended",
};

export const PHASE_COLORS: Record<CyclePhase, any> = {
  default: "default",
  live: "success",
  upcoming: "info",
  past: "default",
};

/**
 * Which week a cycle sits in relative to now. Compared by week key rather than by instant, so the
 * list chip, the delete warning and the editor's lock all derive the phase the same single way and
 * can never disagree about whether a week is still running.
 */
export function getCyclePhase(
  cycle: { isDefault?: boolean; startDate?: string | null },
  now: Date = new Date(),
): CyclePhase {
  if (cycle.isDefault || !cycle.startDate) return "default";

  const start = weekKey(cycle.startDate);
  const current = weekKey(now);

  if (start > current) return "upcoming";

  return start === current ? "live" : "past";
}

export function getDailyRewardCycleColumns(hasActionsColumn: boolean) {
  // `name` deliberately has no renderCell: Table only renders a real <a> for the first
  // non-renderCell column, and that anchor is what makes right-click "Open in new tab" work.
  const columns: any[] = [
    {
      id: "name",
      label: "Name",
      type: "string",
    },
    {
      id: "type",
      label: "Type",
      alignHead: "center",
      alignBody: "center",
      renderCell: (row: CycleRow) => (row.isDefault ? "Default" : "Weekly"),
    },
    {
      id: "week",
      label: "Week",
      alignHead: "center",
      alignBody: "center",
      renderCell: (row: CycleRow) => {
        if (row.isDefault) {
          return (
            <Typography variant="body2" color="text.secondary">
              Every week with no cycle of its own
            </Typography>
          );
        }

        const phase = getCyclePhase(row);

        return (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 1,
            }}
          >
            <span>{formatWeekRange(row.startDate)}</span>
            <Chip
              size="small"
              variant="outlined"
              label={PHASE_LABELS[phase]}
              color={PHASE_COLORS[phase]}
            />
          </Box>
        );
      },
    },
    {
      id: "operators",
      label: "Operators",
      alignHead: "center",
      alignBody: "center",
      renderCell: (row: CycleRow) =>
        row.isDefault
          ? "All"
          : row.operators?.length
            ? String(row.operators.length)
            : "All",
    },
    {
      id: "status",
      label: "Status",
      alignHead: "center",
      alignBody: "center",
      renderCell: (row: CycleRow) => (
        <Chip
          size="small"
          label={row.isActive ? "Active" : "Inactive"}
          color={row.isActive ? "success" : "default"}
        />
      ),
    },
  ];

  if (hasActionsColumn) columns.push({ id: "actions", label: "Actions" });

  return columns;
}
